/* eslint-disable */
// @ts-nocheck
import Papa from 'papaparse';
import type { DayOfWeekName, ProductImage, SimpleItem } from '@/app/shared/types';
import { getSheetRows, isSheetsMode, SHEET_NAMES } from '@/app/shared/sheets';
type Constraint = Record<string, unknown>;
type InsufficientReplenishmentOverviewItem = Record<string, unknown>;
type InsufficientReplenishmentSummary = Record<string, unknown>;
type ReplenishmentInventory = Record<string, unknown>;
type ReplenishmentLocation = Record<string, unknown>;
type ReplenishmentOverview = Record<string, unknown>;
type ReplenishmentOverviewItem = Record<string, unknown>;
type ReplenishmentProduct = Record<string, unknown>;
type ReplenishmentProductLocation = Record<string, unknown>;
type ReplenishmentProductSkuDetails = Record<string, unknown>;
type ReplenishmentProductSkuDetailsInLocation = Record<string, unknown>;
type ReplenishmentProductWithDetails = Record<string, unknown>;
type ReplenishmentLocationType = string;
type ReplenishmentInventoryDetails = Record<string, unknown>;
const flatCsvModules = import.meta.glob('/src/mock-data/replenishment-flat.csv', {
  as: 'raw',
  eager: true,
});
const flatCsv = (Object.values(flatCsvModules)[0] as string | undefined) ?? '';
export function hasFlatCsvData() {
  return Boolean(flatCsv.trim());
}
type FlatRow = {
  store: string;
  storeId: string;
  sku: string;
  skuId: string;
  skuDescription: string | null;
  product: string;
  productId: string;
  style: string | null;
  size: string | null;
  color: string | null;
  category: string | null;
  assortmentGroup: string | null;
  department: string | null;
  departmentId: string | null;
  unitPrice: number | null;
  brand: string | null;
  season: string | null;
  replenishment: number;
  replenishmentTime: number | null;
  shipmentDaysRaw: string | null;
  optimalStock: number | null;
  optimalStockVariance: number | null;
  optimalStockVariancePercent: number | null;
  inventoryNeeded: number | null;
  stock: number | null;
  siteQty: number | null;
  whInventory: number | null;
  sold: number | null;
  salesRate: number | null;
  coverage: number | null;
  replenishmentGap: number | null;
  constraintsRaw: string | null;
  specialEventRaw: string | null;
  daysSinceAllocation: number | null;
  masterSku: string | null;
  masterSkuId: string | null;
  lastUpdatedRaw: string | null;
  imageUrl: string | null;
  locationTypeRaw: string | null;
};
type FlatIndex = {
  rows: FlatRow[];
  byStore: Map<string, FlatRow[]>;
  byProduct: Map<string, FlatRow[]>;
  bySku: Map<string, FlatRow[]>;
  byStoreProduct: Map<string, Map<string, FlatRow[]>>;
  byProductStore: Map<string, Map<string, FlatRow[]>>;
  productImages: Map<string, ProductImage[]>;
  options: {
    locations: SimpleItem[];
    skus: SimpleItem[];
    products: SimpleItem[];
    brands: SimpleItem[];
    seasons: SimpleItem[];
    styles: SimpleItem[];
    categories: SimpleItem[];
    departments: SimpleItem[];
    regions: SimpleItem[];
    cities: SimpleItem[];
    specialEvents: SimpleItem[];
  };
};
const LOCATION_TYPE_VALUES: Record<string, ReplenishmentLocationType> = {
  store: 'store',
  warehouse: 'warehouse',
  wh: 'warehouse',
};
const CONSTRAINT_MAP: Record<string, Constraint> = {
  pack_constraint: 'pack_constraint',
  shipment_schedule: 'shipment_schedule',
  insufficient: 'insufficient',
  depleted: 'depleted',
  depleted_wh: 'depleted',
};
const CITY_OPTIONS = [
  'New York',
  'Los Angeles',
  'Chicago',
  'Houston',
  'Miami',
  'Seattle',
  'Denver',
  'Atlanta',
  'Boston',
  'San Francisco',
];
const REGION_OPTIONS = ['Northeast', 'West', 'Midwest', 'South', 'Southwest', 'Northwest'];
let loggedImageSamples = 0;
const DAY_TOKENS: [string, DayOfWeekName][] = [
  ['Sun', 'sunday'],
  ['Mon', 'monday'],
  ['Tue', 'tuesday'],
  ['Wed', 'wednesday'],
  ['Thu', 'thursday'],
  ['Fri', 'friday'],
  ['Sat', 'saturday'],
];
let flatRowsCache: FlatRow[] | null = null;
let flatIndexCache: FlatIndex | null = null;
function normalizeString(value: unknown) {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
}
function parseNumber(value: unknown) {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const cleaned = String(value).replace(/[$,%]/g, '').replace(/,/g, '').trim();
  if (!cleaned) {
    return null;
  }
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}
function parseList(value: string | null) {
  if (!value) {
    return [];
  }
  return value
    .split(/[;,|]/g)
    .map((item) => item.trim())
    .filter(Boolean);
}
function parseShipmentDays(value: string | null): DayOfWeekName[] {
  if (!value) {
    return [];
  }
  const tokens =
    value.includes(',') || value.includes(' ')
      ? value.split(/[\s,]+/g)
      : DAY_TOKENS.map(([token]) => token).filter((token) => value.includes(token));
  const mapped = tokens
    .map((token) => token.slice(0, 3).toLowerCase())
    .map((token) => DAY_TOKENS.find(([abbr]) => abbr.toLowerCase() === token)?.[1])
    .filter(Boolean) as DayOfWeekName[];
  return mapped.length ? mapped : [];
}
function parseConstraints(value: string | null): Constraint[] {
  const raw = parseList(value);
  const mapped = raw
    .map((item) => item.trim().toLowerCase())
    .map((item) => CONSTRAINT_MAP[item])
    .filter(Boolean) as Constraint[];
  return Array.from(new Set(mapped));
}
function parseSpecialEvents(value: string | null) {
  return Array.from(new Set(parseList(value)));
}
function parseLastUpdated(value: string | null) {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const isoDateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnly) {
    const [, year, month, day] = isoDateOnly;
    return Math.floor(Date.UTC(Number(year), Number(month) - 1, Number(day)) / 1000);
  }
  const slashOrDash = trimmed.match(
    /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (slashOrDash) {
    const [, first, second, year, hours = '0', minutes = '0', seconds = '0'] = slashOrDash;
    const firstNum = Number(first);
    const secondPartNum = Number(second);
    const yearNum = Number(year);
    const hourNum = Number(hours);
    const minuteNum = Number(minutes);
    const secondNum = Number(seconds);
    const dayFirst = firstNum > 12;
    const monthNum = dayFirst ? secondPartNum : firstNum;
    const dayNum = dayFirst ? firstNum : secondPartNum;
    return Math.floor(
      Date.UTC(yearNum, monthNum - 1, dayNum, hourNum, minuteNum, secondNum) / 1000,
    );
  }
  const dateTimeNoTz = trimmed.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)$/);
  if (dateTimeNoTz) {
    const [, datePart, timePart] = dateTimeNoTz;
    const ms = Date.parse(`${datePart}T${timePart}Z`);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }
  const monthNames: Record<string, number> = {
    jan: 1,
    january: 1,
    feb: 2,
    february: 2,
    mar: 3,
    march: 3,
    apr: 4,
    april: 4,
    may: 5,
    jun: 6,
    june: 6,
    jul: 7,
    july: 7,
    aug: 8,
    august: 8,
    sep: 9,
    sept: 9,
    september: 9,
    oct: 10,
    october: 10,
    nov: 11,
    november: 11,
    dec: 12,
    december: 12,
  };
  const monthNameFirst = trimmed.match(
    /^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (monthNameFirst) {
    const [, monthName, day, year, hours = '0', minutes = '0', seconds = '0'] = monthNameFirst;
    const monthNum = monthNames[monthName.toLowerCase()];
    if (monthNum) {
      return Math.floor(
        Date.UTC(
          Number(year),
          monthNum - 1,
          Number(day),
          Number(hours),
          Number(minutes),
          Number(seconds),
        ) / 1000,
      );
    }
  }
  const monthNameSecond = trimmed.match(
    /^(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (monthNameSecond) {
    const [, day, monthName, year, hours = '0', minutes = '0', seconds = '0'] = monthNameSecond;
    const monthNum = monthNames[monthName.toLowerCase()];
    if (monthNum) {
      return Math.floor(
        Date.UTC(
          Number(year),
          monthNum - 1,
          Number(day),
          Number(hours),
          Number(minutes),
          Number(seconds),
        ) / 1000,
      );
    }
  }
  const ms = Date.parse(trimmed);
  if (!Number.isFinite(ms)) {
    return null;
  }
  return Math.floor(ms / 1000);
}
function hashString(value: string) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 1000000;
  }
  return hash;
}
function getCityRegion(storeId: string) {
  const hash = hashString(storeId);
  const city = CITY_OPTIONS[hash % CITY_OPTIONS.length];
  const region = REGION_OPTIONS[hash % REGION_OPTIONS.length];
  return { city, region };
}
function normalizeImageUrl(url: string): string {
  const trimmed = url.trim();
  const imageFormula = trimmed.match(/^=IMAGE\((.+)\)$/i);
  if (imageFormula) {
    const rawArgs = imageFormula[1] ?? '';
    const firstArg = rawArgs.split(',')[0] ?? '';
    const stripped = firstArg.trim().replace(/^"|"$/g, '');
    return normalizeImageUrl(stripped);
  }
  if (!trimmed.includes('drive.google.com') && !trimmed.includes('googleusercontent.com')) {
    return trimmed;
  }
  const idMatch = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/);
  const idFromPath = idMatch ? idMatch[1] : null;
  if (idFromPath) {
    return `https://lh3.googleusercontent.com/d/${idFromPath}`;
  }
  try {
    const parsed = new URL(trimmed);
    const idFromQuery = parsed.searchParams.get('id');
    if (idFromQuery) {
      return `https://lh3.googleusercontent.com/d/${idFromQuery}`;
    }
  } catch {}
  return trimmed;
}
function extractDriveId(url: string) {
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match?.[1]) {
    return match[1];
  }
  try {
    const parsed = new URL(url);
    const idFromQuery = parsed.searchParams.get('id');
    if (idFromQuery) {
      return idFromQuery;
    }
  } catch {}
  return null;
}
function buildImages(url: string | null) {
  if (!url) {
    return [];
  }
  const normalized = normalizeImageUrl(url);
  const domainMatch = normalized.match(/^https?:\/\/([^/]+)/);
  const domain = domainMatch ? domainMatch[1] : '';
  const images: ProductImage[] = [
    {
      public: normalized,
      thumb: normalized,
      domain,
    },
  ];
  const driveId = extractDriveId(url) ?? extractDriveId(normalized);
  if (driveId) {
    const fallback = `https://drive.google.com/uc?export=view&id=${driveId}`;
    if (fallback !== normalized) {
      images.push({
        public: fallback,
        thumb: fallback,
        domain: 'drive.google.com',
      });
    }
  }
  if (loggedImageSamples < 3 && typeof window !== 'undefined' && (import.meta as any)?.env?.DEV) {
    loggedImageSamples += 1;
    console.info('[demo images][replenishment]', {
      raw: url,
      normalized,
      images,
    });
  }
  return images satisfies ProductImage[];
}
function getField(row: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      const value = row[key];
      if (value != null && String(value).trim() !== '') {
        return value;
      }
    }
  }
  return null;
}
function getImageField(row: Record<string, unknown>) {
  const value = getField(row, [
    'Image',
    'Image URL',
    'Image Url',
    'Image Link',
    'Image link',
    'Image_URL',
  ]);
  if (value != null) {
    return value;
  }
  const keyMatch = Object.keys(row).find((key) => {
    const normalized = key.trim().toLowerCase();
    return (
      normalized === 'image' ||
      normalized === 'image url' ||
      normalized === 'image_url' ||
      normalized === 'image link' ||
      normalized === 'image_link'
    );
  });
  return keyMatch ? row[keyMatch] : null;
}
function normalizeRow(row: Record<string, unknown>): FlatRow {
  const store = normalizeString(getField(row, ['Store', 'store'])) ?? '';
  const storeId = normalizeString(getField(row, ['Store ID', 'store_id', 'StoreID'])) ?? store;
  const sku = normalizeString(getField(row, ['SKU', 'sku'])) ?? '';
  const skuId = normalizeString(getField(row, ['SKU ID', 'sku_id', 'Sku ID'])) ?? sku;
  const product = normalizeString(getField(row, ['Product', 'product'])) ?? '';
  const productId =
    normalizeString(getField(row, ['Product ID', 'product_id', 'ProductID'])) ?? product;
  return {
    store,
    storeId,
    sku,
    skuId,
    skuDescription: normalizeString(getField(row, ['SKU description', 'SKU Description'])),
    product,
    productId,
    style: normalizeString(getField(row, ['Style'])),
    size: normalizeString(getField(row, ['Size'])),
    color: normalizeString(getField(row, ['Color'])),
    category: normalizeString(getField(row, ['Category'])),
    assortmentGroup: normalizeString(getField(row, ['AG', 'Assortment Group'])),
    department: normalizeString(getField(row, ['Department'])),
    departmentId: normalizeString(getField(row, ['Department ID', 'department_id'])),
    unitPrice: parseNumber(getField(row, ['Unit price', 'Unit Price'])),
    brand: normalizeString(getField(row, ['Brand'])),
    season: normalizeString(getField(row, ['Season'])),
    replenishment: parseNumber(getField(row, ['Replenishment'])) ?? 0,
    replenishmentTime: parseNumber(getField(row, ['Replenishment time', 'Replenishment Time'])),
    shipmentDaysRaw: normalizeString(getField(row, ['Shipment days', 'Shipment Days'])),
    optimalStock: parseNumber(getField(row, ['Optimal stock', 'Optimal Stock'])),
    optimalStockVariance: parseNumber(
      getField(row, ['Optimal stock variance', 'Optimal Stock Variance']),
    ),
    optimalStockVariancePercent: parseNumber(
      getField(row, ['Optimal stock variance (%)', 'Optimal Stock Variance (%)']),
    ),
    inventoryNeeded: parseNumber(getField(row, ['Inventory needed', 'Inventory Needed'])),
    stock: parseNumber(getField(row, ['Stock'])),
    siteQty: parseNumber(getField(row, ['At site', 'At Site'])),
    whInventory: parseNumber(getField(row, ['WH inventory', 'WH Inventory'])),
    sold: parseNumber(getField(row, ['Sold'])),
    salesRate: parseNumber(getField(row, ['Sales rate', 'Sales Rate'])),
    coverage: parseNumber(getField(row, ['Coverage'])),
    replenishmentGap: parseNumber(getField(row, ['Replenishment Gap'])),
    constraintsRaw: normalizeString(getField(row, ['Constraints'])),
    specialEventRaw: normalizeString(getField(row, ['Special Event', 'Special event'])),
    daysSinceAllocation: parseNumber(
      getField(row, ['Days since allocation', 'Days Since Allocation']),
    ),
    masterSku: normalizeString(getField(row, ['Master SKU', 'Master Sku'])),
    masterSkuId: normalizeString(getField(row, ['Master SKU ID', 'Master Sku ID'])),
    lastUpdatedRaw: normalizeString(getField(row, ['Last updated', 'Last Updated'])),
    imageUrl: normalizeString(getImageField(row)),
    locationTypeRaw: normalizeString(getField(row, ['Location Type', 'location_type'])),
  };
}
async function loadFlatRows() {
  if (flatRowsCache) {
    return flatRowsCache;
  }
  if (isSheetsMode()) {
    const rows = await getSheetRows<Record<string, unknown>>(SHEET_NAMES.replenishmentFlat);
    if (rows.length) {
      flatRowsCache = rows.map((row) => normalizeRow(row));
      return flatRowsCache;
    }
  }
  if (!flatCsv.trim()) {
    flatRowsCache = [];
    return flatRowsCache;
  }
  flatRowsCache = parseCsvRows(flatCsv);
  return flatRowsCache;
}
function parseCsvRows(csv: string) {
  if (!csv.trim()) {
    return [] as FlatRow[];
  }
  const parsed = Papa.parse<Record<string, string>>(csv, {
    header: true,
    skipEmptyLines: true,
  });
  if (parsed.errors.length) {
    const first = parsed.errors[0];
    throw new Error(`Failed to parse flat CSV: ${first.message}`);
  }
  return parsed.data.map((row) => normalizeRow(row));
}
function listTopUnique(rows: FlatRow[], getter: (row: FlatRow) => string | null, limit = 3) {
  const values = rows.map((row) => getter(row)).filter(Boolean) as string[];
  const unique = Array.from(new Set(values));
  return unique.slice(0, limit);
}
function sumNumbers(rows: FlatRow[], getter: (row: FlatRow) => number | null) {
  return rows.reduce((acc, row) => acc + (getter(row) ?? 0), 0);
}
function averageNumbers(rows: FlatRow[], getter: (row: FlatRow) => number | null) {
  const values = rows.map((row) => getter(row)).filter((value) => value != null) as number[];
  if (!values.length) {
    return 0;
  }
  return values.reduce((acc, value) => acc + value, 0) / values.length;
}
function countDistinct(rows: FlatRow[], getter: (row: FlatRow) => string | null) {
  return new Set(rows.map((row) => getter(row)).filter(Boolean)).size;
}
function uniqueValues(rows: FlatRow[], getter: (row: FlatRow) => string | null) {
  return Array.from(new Set(rows.map((row) => getter(row)).filter(Boolean))) as string[];
}
function getLocationType(row: FlatRow): ReplenishmentLocationType {
  const raw = row.locationTypeRaw?.toLowerCase() ?? '';
  return LOCATION_TYPE_VALUES[raw] ?? 'store';
}
function getExpectedCoverage(coverage: number, seed: string) {
  const bump = 0.1 + (hashString(seed) % 6) / 100;
  return coverage * (1 + bump);
}
function toSimpleItems(values: string[]) {
  return [...values]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({
      id: value,
      name: value,
    }));
}
async function buildFlatIndex(): Promise<FlatIndex> {
  if (flatIndexCache) {
    return flatIndexCache;
  }
  const rows = await loadFlatRows();
  const byStore = new Map<string, FlatRow[]>();
  const byProduct = new Map<string, FlatRow[]>();
  const bySku = new Map<string, FlatRow[]>();
  const byStoreProduct = new Map<string, Map<string, FlatRow[]>>();
  const byProductStore = new Map<string, Map<string, FlatRow[]>>();
  const productImages = new Map<string, ProductImage[]>();
  rows.forEach((row) => {
    if (!byStore.has(row.storeId)) {
      byStore.set(row.storeId, []);
    }
    byStore.get(row.storeId)?.push(row);
    if (!byProduct.has(row.productId)) {
      byProduct.set(row.productId, []);
    }
    byProduct.get(row.productId)?.push(row);
    if (!bySku.has(row.skuId)) {
      bySku.set(row.skuId, []);
    }
    bySku.get(row.skuId)?.push(row);
    if (!byStoreProduct.has(row.storeId)) {
      byStoreProduct.set(row.storeId, new Map());
    }
    const storeProducts = byStoreProduct.get(row.storeId) as Map<string, FlatRow[]>;
    if (!storeProducts.has(row.productId)) {
      storeProducts.set(row.productId, []);
    }
    storeProducts.get(row.productId)?.push(row);
    if (!byProductStore.has(row.productId)) {
      byProductStore.set(row.productId, new Map());
    }
    const productStores = byProductStore.get(row.productId) as Map<string, FlatRow[]>;
    if (!productStores.has(row.storeId)) {
      productStores.set(row.storeId, []);
    }
    productStores.get(row.storeId)?.push(row);
    if (!productImages.has(row.productId) && row.imageUrl) {
      productImages.set(row.productId, buildImages(row.imageUrl));
    }
  });
  const locations = Array.from(byStore.values()).map((storeRows) => {
    const first = storeRows[0];
    return {
      id: first.storeId,
      name: first.store,
      external_id: first.storeId,
    };
  });
  const products = Array.from(byProduct.values()).map((productRows) => {
    const first = productRows[0];
    return {
      id: first.productId,
      name: first.product,
      external_id: first.productId,
    };
  });
  const skus = Array.from(bySku.values()).map((skuRows) => {
    const first = skuRows[0];
    return {
      id: first.skuId,
      name: first.sku,
      external_id: first.skuId,
    };
  });
  locations.sort((a, b) => a.name.localeCompare(b.name));
  products.sort((a, b) => a.name.localeCompare(b.name));
  skus.sort((a, b) => a.name.localeCompare(b.name));
  const options = {
    locations,
    skus,
    products,
    brands: toSimpleItems(uniqueValues(rows, (row) => row.brand)),
    seasons: toSimpleItems(uniqueValues(rows, (row) => row.season)),
    styles: toSimpleItems(uniqueValues(rows, (row) => row.style)),
    categories: toSimpleItems(uniqueValues(rows, (row) => row.category)),
    departments: toSimpleItems(uniqueValues(rows, (row) => row.department)),
    regions: toSimpleItems(
      Array.from(new Set(locations.map((item) => getCityRegion(item.id).region))),
    ),
    cities: toSimpleItems(
      Array.from(new Set(locations.map((item) => getCityRegion(item.id).city))),
    ),
    specialEvents: toSimpleItems(
      Array.from(new Set(rows.flatMap((row) => parseSpecialEvents(row.specialEventRaw)))),
    ),
  };
  flatIndexCache = {
    rows,
    byStore,
    byProduct,
    bySku,
    byStoreProduct,
    byProductStore,
    productImages,
    options,
  };
  return flatIndexCache;
}
export async function getFlatInventoryOptions() {
  const { options } = await buildFlatIndex();
  return options;
}
export async function getReplenishmentLocationsFromFlat() {
  const { byStore } = await buildFlatIndex();
  return Array.from(byStore.entries()).map(([storeId, storeRows]) => {
    const first = storeRows[0];
    const { city, region } = getCityRegion(storeId);
    const replenishment = sumNumbers(storeRows, (row) => row.replenishment);
    const sold = sumNumbers(storeRows, (row) => row.sold);
    const coverage = sumNumbers(storeRows, (row) => row.coverage);
    const whZeroRows = storeRows.filter((row) => (row.whInventory ?? 0) === 0);
    return {
      id: storeId,
      account_id: 'demo',
      deleted_at: 0,
      name: first.store,
      description: '',
      external_id: storeId,
      location_type: getLocationType(first),
      region,
      city,
      source_locations: [],
      replenishment_time: [averageNumbers(storeRows, (row) => row.replenishmentTime)],
      classifications: listTopUnique(storeRows, (row) => row.category),
      brands: listTopUnique(storeRows, (row) => row.brand),
      replenishment,
      sold,
      coverage,
      coverage_depleted: sumNumbers(whZeroRows, (row) => row.coverage),
      avg_daily_sales: sumNumbers(storeRows, (row) => row.salesRate),
      avg_daily_sales_depleted: sumNumbers(whZeroRows, (row) => row.salesRate),
      constraints: Array.from(
        new Set(storeRows.flatMap((row) => parseConstraints(row.constraintsRaw))),
      ),
      total_products: countDistinct(storeRows, (row) => row.productId),
      total_skus: countDistinct(storeRows, (row) => row.skuId),
      sold_depleted: sumNumbers(whZeroRows, (row) => row.sold),
      expected_coverage: getExpectedCoverage(coverage, storeId),
      weekly_inventory_needed_ratio: null,
    } satisfies ReplenishmentLocation;
  });
}
export async function getReplenishmentProductsFromFlat() {
  const { byProduct, productImages } = await buildFlatIndex();
  return Array.from(byProduct.entries()).map(([productId, productRows]) => {
    const first = productRows[0];
    return {
      id: productId,
      external_id: productId,
      name: first.product,
      images: productImages.get(productId) ?? [],
      avg_price: Math.max(...productRows.map((row) => row.unitPrice ?? 0)),
      sale_rate: sumNumbers(productRows, (row) => row.salesRate),
      sold: sumNumbers(productRows, (row) => row.sold),
      total_replenishment: sumNumbers(productRows, (row) => row.replenishment),
    } satisfies ReplenishmentProduct;
  });
}
export async function getReplenishmentProductDetailsFromFlat() {
  const { byProduct, productImages } = await buildFlatIndex();
  return Array.from(byProduct.entries()).map(([productId, productRows]) => {
    const first = productRows[0];
    const avgPrice = Math.max(...productRows.map((row) => row.unitPrice ?? 0));
    return {
      id: productId,
      name: first.product,
      external_id: productId,
      categories: uniqueValues(productRows, (row) => row.category),
      brands: uniqueValues(productRows, (row) => row.brand),
      markets: [],
      seasons: uniqueValues(productRows, (row) => row.season),
      styles: uniqueValues(productRows, (row) => row.style),
      colors: uniqueValues(productRows, (row) => row.color),
      avg_cost: avgPrice ? avgPrice * 0.6 : 0,
      avg_price: avgPrice,
      images: productImages.get(productId) ?? [],
      sold: sumNumbers(productRows, (row) => row.sold),
      sale_rates: sumNumbers(productRows, (row) => row.salesRate),
      total_replenishment: sumNumbers(productRows, (row) => row.replenishment),
    } satisfies ReplenishmentProductWithDetails;
  });
}
export async function getReplenishmentInventoriesFromFlat() {
  const { rows } = await buildFlatIndex();
  return rows.map((row) => {
    const inventoryId = `${row.storeId}:${row.skuId}`;
    return {
      id: inventoryId,
      replenishment_id: inventoryId,
      location_id: row.storeId,
      location_name: row.store,
      location_external_id: row.storeId,
      last_replenishment_date: null,
      sku_name: row.sku,
      sku_id: row.skuId,
      sku_external_id: row.skuId,
      sku_description: row.skuDescription,
      assortment_group: row.assortmentGroup ?? '',
      unit_price: row.unitPrice,
      product_name: row.product,
      product_id: row.productId,
      product_external_id: row.productId,
      style_names: row.style ? [row.style] : null,
      color_names: row.color ? [row.color] : null,
      category_names: row.category ? [row.category] : null,
      brand_names: row.brand ? [row.brand] : null,
      season_names: row.season ? [row.season] : null,
      target_market_names: null,
      size_name: row.size,
      replenishment: row.replenishment,
      replenishment_time: row.replenishmentTime ?? 0,
      shipment_days: parseShipmentDays(row.shipmentDaysRaw),
      optimal_stock: row.optimalStock,
      optimal_stock_variance: row.optimalStockVariance,
      optimal_stock_variance_percentage: row.optimalStockVariancePercent,
      inventory_needed: row.inventoryNeeded,
      stock: row.stock,
      wh_inventory: row.whInventory,
      sold: row.sold,
      sale_rate: row.salesRate,
      site_qty: row.siteQty,
      transit_qty: null,
      coverage: row.coverage,
      replenishment_gap: row.replenishmentGap,
      pack_constraint: null,
      constraints: parseConstraints(row.constraintsRaw),
      special_event_names: parseSpecialEvents(row.specialEventRaw),
      days_since_allocation: row.daysSinceAllocation,
      department_id: row.departmentId ?? '',
      department: row.department,
      master_optimal_stock: null,
      master_sku_external_id: row.masterSkuId,
      master_sku_name: row.masterSku,
      last_updated: parseLastUpdated(row.lastUpdatedRaw),
      recommended_replenishment: row.replenishment,
    } satisfies ReplenishmentInventory;
  });
}
export async function getReplenishmentLastUpdatedAt() {
  const inventories = await getReplenishmentInventoriesFromFlat();
  const maxTimestamp = inventories.reduce((acc, item) => {
    if (!item.last_updated) {
      return acc;
    }
    return Math.max(acc, item.last_updated);
  }, 0);
  if (!maxTimestamp) {
    return null;
  }
  return new Date(maxTimestamp * 1000).toISOString();
}
export async function getReplenishmentProductSkuDetailsFromFlat() {
  const { byProduct } = await buildFlatIndex();
  const result: Record<string, ReplenishmentProductSkuDetails[]> = {};
  Array.from(byProduct.entries()).forEach(([productId, productRows]) => {
    const bySku = new Map<string, FlatRow[]>();
    productRows.forEach((row) => {
      if (!bySku.has(row.skuId)) {
        bySku.set(row.skuId, []);
      }
      bySku.get(row.skuId)?.push(row);
    });
    result[productId] = Array.from(bySku.entries()).map(([skuId, skuRows]) => {
      const first = skuRows[0];
      return {
        external_id: skuId,
        replenishment: sumNumbers(skuRows, (row) => row.replenishment),
        sale_rate: sumNumbers(skuRows, (row) => row.salesRate),
        sold: sumNumbers(skuRows, (row) => row.sold),
        total_replenished_stores: countDistinct(skuRows, (row) => row.storeId),
        constraints: Array.from(
          new Set(skuRows.flatMap((row) => parseConstraints(row.constraintsRaw))),
        ),
        wh_qty: sumNumbers(skuRows, (row) => row.whInventory),
        size: first.size ?? '',
        name: first.sku,
      } satisfies ReplenishmentProductSkuDetails;
    });
  });
  return result;
}
export async function getReplenishmentProductSkuDetailsInLocationFromFlat() {
  const { byStoreProduct } = await buildFlatIndex();
  const result: Record<string, ReplenishmentProductSkuDetailsInLocation[]> = {};
  Array.from(byStoreProduct.entries()).forEach(([storeId, storeProducts]) => {
    Array.from(storeProducts.entries()).forEach(([productId, rows]) => {
      const key = `${productId}:${storeId}`;
      result[key] = rows.map((row) => ({
        id: `${storeId}:${row.skuId}`,
        sku_id: row.skuId,
        sku_external_id: row.skuId,
        sku_name: row.sku,
        size: row.size ?? '',
        replenishment: row.replenishment,
        sale_rate: row.salesRate ?? 0,
        site_qty: row.siteQty ?? 0,
        transit_qty: 0,
        optimal_stock: row.optimalStock ?? 0,
        sold: row.sold ?? 0,
        wh_names: [],
        next_replenishment: '',
        wh_qty: row.whInventory ?? 0,
        coverage: row.coverage ?? 0,
        expected_coverage: getExpectedCoverage(row.coverage ?? 0, `${productId}:${storeId}`),
        constraints: parseConstraints(row.constraintsRaw),
        last_in_on: null,
        master_sku_external_id: row.masterSkuId,
        master_sku_name: row.masterSku,
      }));
    });
  });
  return result;
}
export async function getReplenishmentProductLocationsFromFlat() {
  const { byProductStore } = await buildFlatIndex();
  const result: Record<string, ReplenishmentProductLocation[]> = {};
  Array.from(byProductStore.entries()).forEach(([productId, productStores]) => {
    result[productId] = Array.from(productStores.entries()).map(([storeId, rows]) => {
      const first = rows[0];
      const { city, region } = getCityRegion(storeId);
      return {
        id: storeId,
        city,
        region,
        external_id: storeId,
        name: first.store,
        location_type: getLocationType(first),
        replenishment: sumNumbers(rows, (row) => row.replenishment),
        sold: sumNumbers(rows, (row) => row.sold),
        sale_rate: sumNumbers(rows, (row) => row.salesRate),
        total_skus: countDistinct(rows, (row) => row.skuId),
        inventory_ids: rows.map((row) => `${storeId}:${row.skuId}`),
      } satisfies ReplenishmentProductLocation;
    });
  });
  return result;
}
export async function getReplenishmentLocationProductsFromFlat() {
  const { byStore, productImages } = await buildFlatIndex();
  const result: Record<string, ReplenishmentProduct[]> = {};
  Array.from(byStore.entries()).forEach(([storeId, storeRows]) => {
    const byProduct = new Map<string, FlatRow[]>();
    storeRows.forEach((row) => {
      if (!byProduct.has(row.productId)) {
        byProduct.set(row.productId, []);
      }
      byProduct.get(row.productId)?.push(row);
    });
    result[storeId] = Array.from(byProduct.entries()).map(([productId, productRows]) => {
      const first = productRows[0];
      return {
        id: productId,
        external_id: productId,
        name: first.product,
        images: productImages.get(productId) ?? [],
        avg_price: Math.max(...productRows.map((row) => row.unitPrice ?? 0)),
        sale_rate: sumNumbers(productRows, (row) => row.salesRate),
        sold: sumNumbers(productRows, (row) => row.sold),
        total_replenishment: sumNumbers(productRows, (row) => row.replenishment),
      } satisfies ReplenishmentProduct;
    });
  });
  return result;
}
export async function getReplenishmentOverviewFromFlat() {
  const { byStore } = await buildFlatIndex();
  const result: ReplenishmentOverview = {
    replenishment: [],
    sold: [],
    sold_depleted: [],
  };
  Array.from(byStore.entries()).forEach(([storeId, rows]) => {
    const first = rows[0];
    const replenishmentItem: ReplenishmentOverviewItem = {
      location_id: storeId,
      external_id: storeId,
      name: first.store,
      total: sumNumbers(rows, (row) => row.replenishment),
    };
    const soldItem: ReplenishmentOverviewItem = {
      location_id: storeId,
      external_id: storeId,
      name: first.store,
      total: sumNumbers(rows, (row) => row.sold),
    };
    const soldDepletedItem: ReplenishmentOverviewItem = {
      location_id: storeId,
      external_id: storeId,
      name: first.store,
      total: sumNumbers(
        rows.filter((row) => (row.whInventory ?? 0) === 0),
        (row) => row.sold,
      ),
    };
    result.replenishment.push(replenishmentItem);
    result.sold.push(soldItem);
    result.sold_depleted.push(soldDepletedItem);
  });
  return result;
}
export async function getInsufficientSummaryFromFlat() {
  const { rows } = await buildFlatIndex();
  const result: Record<string, InsufficientReplenishmentSummary> = {};
  rows.forEach((row) => {
    const constraints = parseConstraints(row.constraintsRaw);
    if (!constraints.includes('insufficient')) {
      return;
    }
    const inventoryId = `${row.storeId}:${row.skuId}`;
    if (result[inventoryId]) {
      return;
    }
    result[inventoryId] = {
      id: inventoryId,
      product_name: row.product,
      product_external_id: row.productId,
      external_id: row.skuId,
      name: row.sku,
      size: row.size ?? '',
      wh_name: 'Warehouse',
      wh_external_id: 'warehouse',
      replenishments: row.replenishment,
      supply: row.whInventory ?? 0,
      images: buildImages(row.imageUrl),
      colors: row.color ? [row.color] : [],
      styles: row.style ? [row.style] : [],
      brands: row.brand ? [row.brand] : [],
      categories: row.category ? [row.category] : [],
      seasons: row.season ? [row.season] : [],
      department: row.department ?? '',
      department_external_id: row.departmentId ?? '',
      country: 'US',
      region: getCityRegion(row.storeId).region,
      created_at: new Date().toISOString(),
      price: row.unitPrice ?? 0,
      replenishment_lead_time: row.replenishmentTime ?? 0,
    };
  });
  return result;
}
export async function getInsufficientOverviewFromFlat() {
  const { rows } = await buildFlatIndex();
  const result: Record<string, InsufficientReplenishmentOverviewItem[]> = {};
  rows.forEach((row) => {
    const constraints = parseConstraints(row.constraintsRaw);
    if (!constraints.includes('insufficient')) {
      return;
    }
    const inventoryId = `${row.storeId}:${row.skuId}`;
    if (!result[inventoryId]) {
      result[inventoryId] = [];
    }
    result[inventoryId].push({
      name: row.store,
      site_qty: row.siteQty ?? 0,
      transit_qty: 0,
      replenishment: row.replenishment,
      sale_rate: row.salesRate ?? 0,
      constraints,
    });
  });
  return result;
}
export async function getReplenishmentInventoryDetailsFromFlat(
  id: string,
): Promise<ReplenishmentInventoryDetails | null> {
  const { rows } = await buildFlatIndex();
  const match = rows.find((row) => `${row.storeId}:${row.skuId}` === id);
  if (!match) {
    return null;
  }
  return {
    id,
    sku_external_id: match.skuId,
    size: match.size ?? '',
    replenishment: match.replenishment,
    sale_rate: match.salesRate,
    site_qty: match.siteQty ?? 0,
    transit_qty: 0,
    optimal_stock: match.optimalStock ?? 0,
    sold: match.sold ?? 0,
    wh_names: [],
    next_replenishment: '',
    wh_qty: match.whInventory ?? 0,
    coverage: match.coverage ?? 0,
    expected_coverage: getExpectedCoverage(match.coverage ?? 0, id),
    constraints: parseConstraints(match.constraintsRaw),
    last_in_on: null,
    master_sku_external_id: match.masterSkuId,
    master_sku_name: match.masterSku,
  };
}
